import React, { useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { School, Wallet, CalendarDays, CheckCircle2, Plus, Trash2, ArrowRight, ArrowLeft } from "lucide-react";

const LEVELS = ["Maternelle", "Primaire", "Collège", "Lycée"];
const defYear = () => { const y = new Date().getFullYear(); return `${y}-${y + 1}`; };

const STEPS = [
  { icon: School, label: "Année & Classes" },
  { icon: Wallet, label: "Grilles tarifaires" },
  { icon: CalendarDays, label: "Calendrier de rentrée" },
  { icon: CheckCircle2, label: "Terminé" },
];

export default function OuvertureAnneePage() {
  const [step, setStep] = useState(0);
  const [year, setYear] = useState(defYear());
  const [classes, setClasses] = useState([{ name: "", level: "Primaire", max_students: 40 }]);
  const [created, setCreated] = useState([]);
  const [fees, setFees] = useState({});
  const [rentree, setRentree] = useState({ date: "", title: `Rentrée scolaire ${defYear()}`, location: "Campus Les Écoles Mazid" });
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState({ classes: 0, grilles: 0, event: false });

  const saveClasses = async () => {
    const valid = classes.filter((c) => c.name.trim());
    if (!valid.length) return toast.error("Ajoutez au moins une classe");
    setSaving(true);
    let ok = 0; const names = [];
    for (const c of valid) {
      try {
        await api.post("/pedagogy/classes", { name: c.name.trim(), level: c.level, year, max_students: Number(c.max_students) || null });
        ok += 1; names.push(c.name.trim());
      } catch (e) {
        if (e.response?.status === 409) names.push(c.name.trim());
        else toast.error(`${c.name} : ${e.response?.data?.detail || "erreur"}`);
      }
    }
    setSaving(false);
    setCreated(names);
    setFees(Object.fromEntries(names.map((n) => [n, { registration: 500000, tuition: 4500000 }])));
    setSummary((s) => ({ ...s, classes: ok }));
    if (names.length) { toast.success(`${ok} classe(s) créée(s)`); setStep(1); }
  };

  const saveFees = async () => {
    setSaving(true);
    let ok = 0;
    for (const name of created) {
      const f = fees[name];
      if (!f) continue;
      try {
        await api.post("/scolarite/fee-configs", {
          class_name: name, academic_year: year,
          level: classes.find((c) => c.name.trim() === name)?.level,
          items: [
            { fee_type: "registration", label: "Frais d'inscription", amount_gnf: Number(f.registration) || 0, mandatory: true, recurrence: "one_time" },
            { fee_type: "tuition", label: "Scolarité annuelle", amount_gnf: Number(f.tuition) || 0, mandatory: true, recurrence: "annual" },
          ],
          notes: `Assistant ouverture ${year}`,
        });
        ok += 1;
      } catch (e) { toast.error(`${name} : ${e.response?.data?.detail || "erreur grille"}`); }
    }
    setSaving(false);
    setSummary((s) => ({ ...s, grilles: ok }));
    toast.success(`${ok} grille(s) créée(s) — à valider par le Fondateur (Scolarité → Grilles)`);
    setStep(2);
  };

  const saveRentree = async () => {
    if (!rentree.date) return toast.error("Choisissez la date de rentrée");
    setSaving(true);
    try {
      await api.post("/announcements", {
        title: rentree.title, category: "event", event_date: rentree.date, event_location: rentree.location,
        content: `La rentrée scolaire ${year} aura lieu le ${new Date(rentree.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}. Les inscriptions sont ouvertes.`,
      });
      setSummary((s) => ({ ...s, event: true }));
      toast.success("Événement de rentrée publié sur le site");
      setStep(3);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur événement"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-3xl space-y-6" data-testid="ouverture-annee-page">
      <div>
        <h1 className="font-heading font-extrabold text-3xl text-[#1E3A8A]">Assistant — Ouverture de l&apos;année scolaire</h1>
        <p className="text-slate-500 mt-1">Créez classes, grilles tarifaires et calendrier de rentrée en 3 étapes guidées.</p>
      </div>

      <div className="flex items-center gap-2">
        {STEPS.map((s, i) => (
          <div key={i} className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold ${i === step ? "bg-[#1E3A8A] text-white" : i < step ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>
            <s.icon className="h-4 w-4" /> {s.label}
          </div>
        ))}
      </div>

      {step === 0 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="wizard-step-classes">
          <label className="block text-sm font-semibold">Année scolaire
            <Input value={year} onChange={(e) => setYear(e.target.value)} className="mt-1 max-w-xs" data-testid="wizard-year-input" />
          </label>
          <div className="space-y-2">
            {classes.map((c, i) => (
              <div key={i} className="flex flex-wrap gap-2 items-center">
                <Input placeholder="Nom (ex: Primaire CP1 A)" value={c.name} className="flex-1 min-w-[180px]" data-testid={`wizard-class-name-${i}`}
                  onChange={(e) => setClasses(classes.map((x, j) => (j === i ? { ...x, name: e.target.value } : x)))} />
                <select value={c.level} className="border rounded-lg px-2 py-2 text-sm"
                  onChange={(e) => setClasses(classes.map((x, j) => (j === i ? { ...x, level: e.target.value } : x)))}>
                  {LEVELS.map((l) => <option key={l}>{l}</option>)}
                </select>
                <Input type="number" title="Capacité max" value={c.max_students} className="w-24"
                  onChange={(e) => setClasses(classes.map((x, j) => (j === i ? { ...x, max_students: e.target.value } : x)))} />
                <button onClick={() => setClasses(classes.filter((_, j) => j !== i))} className="text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setClasses([...classes, { name: "", level: "Primaire", max_students: 40 }])} data-testid="wizard-add-class-btn">
              <Plus className="h-4 w-4 mr-1" /> Ajouter une classe
            </Button>
            <Button onClick={saveClasses} disabled={saving} className="bg-[#1E3A8A]" data-testid="wizard-save-classes-btn">
              Créer les classes <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {step === 1 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="wizard-step-fees">
          <p className="text-sm text-slate-500">Montants en GNF par classe. Un plan de paiement en 9 mensualités sera généré automatiquement à chaque inscription validée.</p>
          {created.map((n) => (
            <div key={n} className="flex flex-wrap gap-2 items-center border-b border-slate-100 pb-2">
              <span className="font-semibold text-sm w-44">{n}</span>
              <label className="text-xs">Inscription <Input type="number" value={fees[n]?.registration} className="w-32"
                onChange={(e) => setFees({ ...fees, [n]: { ...fees[n], registration: e.target.value } })} /></label>
              <label className="text-xs">Scolarité annuelle <Input type="number" value={fees[n]?.tuition} className="w-36"
                onChange={(e) => setFees({ ...fees, [n]: { ...fees[n], tuition: e.target.value } })} /></label>
            </div>
          ))}
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(0)}><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Button>
            <Button onClick={saveFees} disabled={saving} className="bg-[#1E3A8A]" data-testid="wizard-save-fees-btn">
              Créer les grilles <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="wizard-step-calendar">
          <label className="block text-sm font-semibold">Date de la rentrée
            <Input type="date" value={rentree.date} onChange={(e) => setRentree({ ...rentree, date: e.target.value })} className="mt-1 max-w-xs" data-testid="wizard-rentree-date" />
          </label>
          <label className="block text-sm font-semibold">Titre de l&apos;événement
            <Input value={rentree.title} onChange={(e) => setRentree({ ...rentree, title: e.target.value })} className="mt-1" />
          </label>
          <label className="block text-sm font-semibold">Lieu
            <Input value={rentree.location} onChange={(e) => setRentree({ ...rentree, location: e.target.value })} className="mt-1" />
          </label>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => setStep(1)}><ArrowLeft className="h-4 w-4 mr-1" /> Retour</Button>
            <Button onClick={saveRentree} disabled={saving} className="bg-[#1E3A8A]" data-testid="wizard-save-rentree-btn">
              Publier la rentrée <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="bg-white rounded-xl border border-emerald-200 p-8 text-center" data-testid="wizard-done">
          <CheckCircle2 className="h-10 w-10 text-emerald-500 mx-auto mb-3" />
          <h2 className="font-heading font-bold text-xl text-[#0F172A]">Année scolaire {year} ouverte !</h2>
          <p className="text-sm text-slate-500 mt-2">
            {summary.classes} classe(s) créée(s) · {summary.grilles} grille(s) tarifaire(s) (à faire valider par le Fondateur dans Scolarité → Grilles) · {summary.event ? "Rentrée publiée sur le site ✓" : ""}
          </p>
          <p className="text-xs text-slate-400 mt-3">Les inscriptions en ligne et directes sont maintenant possibles — chaque validation générera automatiquement le plan de paiement en 9 mensualités.</p>
        </div>
      )}
    </div>
  );
}
